import React, { useRef, useEffect, useCallback } from "react";
import type { CalendarEntry, CalendarDate } from "../lib/dateUtils";
import { usePreferencesStore } from "../stores/preferencesStore";

import "@jpahd/kalendus";

interface LMSCalendarElement extends HTMLElement {
  entries: CalendarEntry[];
  activeDate: CalendarDate;
  _menuOpen: boolean;
  _menuEventDetails?: unknown;
  _viewState: { viewMode: string };
}

interface KalendusWrapperProps {
  entries: CalendarEntry[];
  activeDate?: CalendarDate;
  onActiveDateChange?: (date: CalendarDate, view: string) => void;
  onEventClick?: (detail: { heading: string; content: string }) => void;
}

export function KalendusWrapper({
  entries,
  activeDate,
  onActiveDateChange,
  onEventClick,
}: KalendusWrapperProps) {
  const ref = useRef<LMSCalendarElement>(null);
  const callbackRefs = useRef({ onActiveDateChange, onEventClick });
  callbackRefs.current = { onActiveDateChange, onEventClick };

  const locale = usePreferencesStore((s) => s.locale);
  const firstDayOfWeek = usePreferencesStore((s) => s.firstDayOfWeek);
  const yearDrillTarget = usePreferencesStore((s) => s.yearDrillTarget);
  const yearDensityMode = usePreferencesStore((s) => s.yearDensityMode);
  const weekDayCount = usePreferencesStore((s) => s.weekDayCount);

  // Set entries imperatively whenever they change
  useEffect(() => {
    if (!ref.current) return;
    ref.current.entries = entries;
  }, [entries]);

  // Push activeDate from parent (mini-month navigation) into the component
  useEffect(() => {
    if (!ref.current || !activeDate) return;
    ref.current.activeDate = activeDate;
  }, [activeDate?.year, activeDate?.month, activeDate?.day]);

  // Read kalendus's current activeDate and viewMode, relay to parent
  const relayActiveDate = useCallback(() => {
    queueMicrotask(() => {
      const el = ref.current;
      if (!el) return;
      const date = el.activeDate;
      const view = el._viewState?.viewMode ?? "month";
      callbackRefs.current.onActiveDateChange?.(date, view);
    });
  }, []);

  // Listen for all kalendus navigation and interaction events
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Navigation events — kalendus updates its own state, we just read it after
    const onNav = () => relayActiveDate();

    // open-menu: suppress kalendus popup, open our modal
    const onOpenMenu = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      // Close kalendus's built-in menu so it doesn't stack with ours
      queueMicrotask(() => {
        if (ref.current) {
          ref.current._menuOpen = false;
          ref.current._menuEventDetails = undefined;
        }
      });
      callbackRefs.current.onEventClick?.(detail);
    };

    el.addEventListener("switchdate", onNav);
    el.addEventListener("switchview", onNav);
    el.addEventListener("jumptoday", onNav);
    el.addEventListener("expand", onNav);
    el.addEventListener("open-menu", onOpenMenu);

    return () => {
      el.removeEventListener("switchdate", onNav);
      el.removeEventListener("switchview", onNav);
      el.removeEventListener("jumptoday", onNav);
      el.removeEventListener("expand", onNav);
      el.removeEventListener("open-menu", onOpenMenu);
    };
  }, [relayActiveDate]);

  return React.createElement("lms-calendar", {
    ref,
    locale,
    "first-day-of-week": String(firstDayOfWeek),
    "year-drill-target": yearDrillTarget,
    "year-density-mode": yearDensityMode,
    style: {
      display: "block",
      width: "100%",
      height: "100%",
      "--week-day-count": String(weekDayCount),
    },
  });
}
